import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  customerBucketAccessGrantsQueryKey,
  getCustomerBucketAccessGrants,
  validateCustomerBucketAccessGrant,
} from 'api/customerBucketAccessGrants';
import { CustomerBucketAccessGrant } from 'interfaces/customerBucketAccessGrant';
import { Permission } from 'interfaces/permissionOption';
import { filter, first } from 'lodash';
import React, { useState } from 'react';
import { getHttpStatusFromError, getQueryErrorMessage } from 'utils/httpError';
import { usePermissions } from 'utils/usePermissions';
import { CopyableBlock } from './CopyableCliBlock';

interface ValidationResultMessage {
  copyButtonAriaLabel: string;
  severity: 'success' | 'error';
  text: string;
}

const CustomerGrantValidationStep = ({ studyId }: { studyId: string }) => {
  const { hasPermission } = usePermissions();
  const canUpdateNucleaiUploadPolicy =
    hasPermission(Permission.CreateOwnCustomerBucketAccessGrants) ||
    hasPermission(Permission.EditLabCustomerBucketAccessGrants);
  const [selectedGrantId, setSelectedGrantId] = useState('');
  const [validationMessage, setValidationMessage] = useState<ValidationResultMessage | null>(null);
  const grantsQuery = useQuery({
    queryKey: customerBucketAccessGrantsQueryKey(studyId),
    queryFn: ({ signal }) => getCustomerBucketAccessGrants(studyId, signal),
    enabled: Boolean(studyId),
  });
  const grants = filter(grantsQuery.data || [], (grant) => grant.status !== 'disabled');
  const selectedGrant = grants.find((grant) => grant.id === selectedGrantId) || first(grants);
  const effectiveGrantId = selectedGrant?.id || '';
  const validateButtonLabel = canUpdateNucleaiUploadPolicy
    ? 'Validate and update AWS upload permissions'
    : 'Validate AWS upload permissions';
  const validateMutation = useMutation(() => validateCustomerBucketAccessGrant(studyId, effectiveGrantId), {
    onSuccess: (result) => {
      setValidationMessage({
        copyButtonAriaLabel: 'Copy validation result',
        severity: result.status === 'active' ? 'success' : 'error',
        text:
          result.status === 'active'
            ? 'Nucleai upload setup is ready. Source access and permissions attached to your AWS role or user are managed separately by your AWS administrator.'
            : 'Nucleai upload setup is not ready yet. Please contact Nucleai support.',
      });
    },
    onError: (error) => {
      setValidationMessage(getValidationErrorMessage(error));
    },
  });

  if (grantsQuery.isLoading) {
    return <Typography color="text.secondary">Loading AWS upload access...</Typography>;
  }

  if (grantsQuery.isError) {
    return (
      <Alert severity="error">
        {getQueryErrorMessage(grantsQuery.error, 'AWS upload access could not be loaded.')}
      </Alert>
    );
  }

  if (grants.length === 0) {
    return <Alert severity="info">Configure AWS upload access before validating permissions.</Alert>;
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Validate the AWS role or user before generating an upload command. The previous step records which identity you
        plan to use; this step checks and updates Nucleai&apos;s upload setup. It does not test source access or
        permissions managed in your AWS account.
      </Typography>
      <TextField
        select
        label="AWS role or user"
        value={effectiveGrantId}
        onChange={(event) => {
          setSelectedGrantId(event.target.value);
          setValidationMessage(null);
        }}
        sx={{ maxWidth: 360 }}
      >
        {grants.map((grant: CustomerBucketAccessGrant) => (
          <MenuItem key={grant.id} value={grant.id}>
            {grant.displayName}
          </MenuItem>
        ))}
      </TextField>
      <Button
        variant="outlined"
        startIcon={<CheckCircleOutlineIcon />}
        disabled={!effectiveGrantId || validateMutation.isLoading}
        onClick={() => validateMutation.mutate()}
        sx={{ alignSelf: 'flex-start' }}
      >
        {validateMutation.isLoading ? 'Validating...' : validateButtonLabel}
      </Button>
      {validationMessage ? (
        <Alert severity={validationMessage.severity}>
          <Stack spacing={1}>
            <Typography variant="body2">{validationMessage.text}</Typography>
            {validationMessage.severity !== 'success' && (
              <CopyableBlock
                text={validationMessage.text}
                copyButtonAriaLabel={validationMessage.copyButtonAriaLabel}
              />
            )}
          </Stack>
        </Alert>
      ) : null}
    </Stack>
  );
};

const getValidationErrorMessage = (error: unknown): ValidationResultMessage => {
  const message = getQueryErrorMessage(error, 'Access could not be validated.');
  const isNucleaiBackendIssue =
    message.includes('backend AWS credentials are expired or invalid') ||
    message.includes('Destination policy reconciliation failed') ||
    message === 'Internal Server Error' ||
    getHttpStatusFromError(error) === 500;

  if (isNucleaiBackendIssue) {
    return {
      copyButtonAriaLabel: 'Copy validation result for Nucleai support',
      severity: 'error',
      text: 'AWS upload permission validation failed because Nucleai could not complete an internal AWS setup check. Please contact Nucleai support.',
    };
  }

  return {
    copyButtonAriaLabel: 'Copy validation result for IT',
    severity: 'error',
    text: `AWS upload permission validation failed: ${message}`,
  };
};

export default CustomerGrantValidationStep;
